import { ReactNode } from 'react';
import Icon, { Icons } from '@/components/elements/Icon';
import ActionButton from '@/components/elements/ActionButton';
import { useTranslations } from 'use-intl';
import ConnectButton from '@/presentation/components/widgets/ConnectButton';

const formatError = (e: any): { message: string } => {
  if (e?.errorCode === 'NO_ROUTE' || e?.validationErrors?.[0]?.code === 1004) {
    return { message: 'Insufficient liquidity' };
  }
  if (e?.detail) {
    return { message: e.detail };
  }
  if (e?.message) {
    return { message: e.message };
  }
  return { message: 'Unknown error' };
};

export function SwapReady({
  canFlip,
  showBuy,
  showSell,
  sell,
  buy,
  settings,
  summary,
  error,
  onFlip,
  onSubmit,
  hasQuote,
  hasRequiredBalance,
  isConnected,
  title,
}: {
  canFlip: boolean;
  showBuy: boolean;
  showSell: boolean;
  onFlip: () => void;
  error: any;
  settings: ReactNode;
  sell: ReactNode;
  buy: ReactNode;
  summary: ReactNode;
  onSubmit: () => void;
  hasQuote: boolean;
  hasRequiredBalance: boolean;
  isConnected: boolean;
  title?: ReactNode;
}) {
  const t = useTranslations('Swap');
  const canSwap = hasQuote && isConnected && hasRequiredBalance;

  return (
    <form
      className="space-y-6 w-full p-6 bg-gray-50"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <header className="flex justify-between relative">
        <h1 className="text-xl font-black">{title || t('title')}</h1>
        {settings}
      </header>
      <section className="space-y-2">
        {sell}
        {showBuy && showSell && canFlip && (
          <div className="relative">
            <button
              type="button"
              className="bg-gray-50 rounded-lg w-10 h-10 absolute top-0 -mt-5 flex items-center justify-center left-[50%] -ml-5 border border-transparent hover:border-gray-300 transition-colors"
              onClick={onFlip}
            >
              <Icon
                name={Icons.ARROW_DOWN}
                size={24}
                className="text-gray-500"
              />
            </button>
          </div>
        )}
        {buy}
      </section>
      {!!error && (
        <aside>
          <div className="bg-red-500 p-4 rounded flex gap-4 text-sm break-all text-gray-50">
            <span>
              <Icon name={Icons.GLOBE} />
            </span>
            <span>{formatError(error).message}</span>
          </div>
        </aside>
      )}
      {summary}
      {isConnected ? (
        <ActionButton
          type="submit"
          blendColor="after:bg-gray-100"
          className="w-full"
          disabled={!canSwap}
        >
          {t('swap')}
        </ActionButton>
      ) : (
        <ConnectButton className="w-full" blendColor="after:bg-gray-100" />
      )}
    </form>
  );
}

export default SwapReady;
